/**
 * The Splash screens page's live file checks, run in the browser before any upload
 * (splash-cms §1 part 4, SP-D12, SP-D26). Every refusal is one plain sentence, word for word as
 * the plan writes it. The server re-checks the uploaded file (size, MP4 `ftyp`, Lottie structure)
 * and its own sentences are shown under the File field after upload.
 *
 * Limits are the server's values (`AppSplashService.MaxVideoBytes` / `MaxLottieBytes`):
 * 1 MB = 1,048,576 bytes, so 5 MB = 5,242,880 bytes.
 */
import type { AppSplashMediaType } from "@/types/generated/cms.generated";

export const MB = 1_048_576;
export const MAX_VIDEO_BYTES = 5 * MB;
export const MAX_LOTTIE_BYTES = 1 * MB;
export const MAX_SPLASH_SECONDS = 4;
/** Upright 9:16 (width ÷ height), ± 2 %. */
export const SPLASH_RATIO = 9 / 16;
export const SPLASH_RATIO_TOLERANCE = 0.02;

export const SPLASH_FILE_MESSAGES = {
  videoTooLong: (seconds: string) =>
    `This video is ${seconds} s. Splashes can be up to 4 s, so phones aren't kept waiting.`,
  videoTooBig: (megabytes: string) => `This video is ${megabytes} MB. The limit is 5 MB, so phones download it quickly.`,
  videoWide: (ratio: string) => `This video is wide (${ratio}). Use an upright 9:16 video so it fills the phone screen.`,
  videoShape: (ratio: string) => `This video is ${ratio}. Use an upright 9:16 video so it fills the phone screen.`,
  notLottie: "This isn't a Lottie file.",
  lottieTooBig: "The Lottie file must be 1 MB or less.",
  lottieTooLong: (seconds: string) =>
    `This animation is ${seconds} s. Splashes can be up to 4 s, so phones aren't kept waiting.`,
  otherType: "Use an MP4 video or a Lottie .json file.",
  /** The server's own sentence (`AppSplashService.NotMp4Message`), for a video the browser cannot read. */
  unreadableVideo: "This isn't an MP4 video.",
} as const;

/** The parts of a `File` the check reads; a "Try a file" sample is a plain object of this shape. */
export interface SplashFileLike {
  name: string;
  type: string;
  size: number;
  text?: () => Promise<string>;
}

export interface VideoMetadata {
  durationSeconds: number;
  width: number;
  height: number;
}

export type VideoMetadataLoader = (file: SplashFileLike) => Promise<VideoMetadata>;

export type SplashContentType = "video/mp4" | "application/json";

export type SplashFileCheck =
  | {
      ok: true;
      mediaType: AppSplashMediaType;
      contentType: SplashContentType;
      durationSeconds: number;
      /** e.g. "national-day.mp4 · 3.0 s · 1.4 MB · 1080×1920". */
      summary: string;
    }
  | { ok: false; error: string };

/** Seconds rounded UP to 0.1 s for display, so a refused 4.04 s reads "4.1". */
export function formatSeconds(seconds: number): string {
  // The epsilon keeps an exact tenth (4.1 × 10 = 41.000000000000004) from rounding up again.
  return (Math.ceil(seconds * 10 - 1e-9) / 10).toFixed(1);
}

const megabytes = (bytes: number) => (bytes / MB).toFixed(1);

/** "180 KB" under 1 MB, else "1.4 MB". */
export function formatSplashSize(bytes: number): string {
  return bytes < MB ? `${Math.max(1, Math.round(bytes / 1024))} KB` : `${megabytes(bytes)} MB`;
}

const gcd = (a: number, b: number): number => (b === 0 ? a : gcd(b, a % b));
const oneDecimal = (n: number) => String(Math.round(n * 10) / 10);

/**
 * A video's shape as a ratio people know: small whole ratios as-is (1:1, 4:3), otherwise against
 * 9 on the short side (16:9, 9:19.5, 21.3:9).
 */
export function formatRatio(width: number, height: number): string {
  if (width <= 0 || height <= 0) return `${width}×${height}`;
  const divisor = gcd(width, height);
  const [w, h] = [width / divisor, height / divisor];
  if (w <= 4 && h <= 4) return `${w}:${h}`;
  return width >= height ? `${oneDecimal((width / height) * 9)}:9` : `9:${oneDecimal((height / width) * 9)}`;
}

function kindOf(file: SplashFileLike): "video" | "lottie" | "other" {
  const type = file.type.toLowerCase();
  const name = file.name.toLowerCase();
  if (type === "video/mp4" || (type === "" && name.endsWith(".mp4"))) return "video";
  if (type === "application/json" || name.endsWith(".json")) return "lottie";
  return "other";
}

/** How long the browser gets to read a video's metadata before the file counts as unreadable. */
export const VIDEO_METADATA_TIMEOUT_MS = 10_000;

/**
 * Reads a video's duration and size through a detached `<video>` (metadata only). Rejects when the
 * browser can't read it, or never answers within `VIDEO_METADATA_TIMEOUT_MS` (some codecs and
 * truncated files fire neither event), so the panel is never stuck on "Checking the file…".
 */
export function loadVideoMetadataFromFile(file: Blob): Promise<VideoMetadata> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const video = document.createElement("video");
    const finish = () => {
      window.clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute("src");
      URL.revokeObjectURL(url);
    };
    const timer = window.setTimeout(() => {
      finish();
      reject(new Error("Unreadable video"));
    }, VIDEO_METADATA_TIMEOUT_MS);
    video.preload = "metadata";
    video.muted = true;
    video.onloadedmetadata = () => {
      const metadata = { durationSeconds: video.duration, width: video.videoWidth, height: video.videoHeight };
      finish();
      resolve(metadata);
    };
    video.onerror = () => {
      finish();
      reject(new Error("Unreadable video"));
    };
    video.src = url;
  });
}

async function checkVideo(file: SplashFileLike, loadVideoMetadata: VideoMetadataLoader): Promise<SplashFileCheck> {
  if (file.size > MAX_VIDEO_BYTES) return { ok: false, error: SPLASH_FILE_MESSAGES.videoTooBig(megabytes(file.size)) };

  let metadata: VideoMetadata;
  try {
    metadata = await loadVideoMetadata(file);
  } catch {
    return { ok: false, error: SPLASH_FILE_MESSAGES.unreadableVideo };
  }

  const { durationSeconds, width, height } = metadata;
  // No usable metadata (an audio-only .mp4, a NaN or endless duration): every comparison below
  // would be false and the file would pass, so phones would play a blank splash.
  if (!Number.isFinite(durationSeconds) || width <= 0 || height <= 0) {
    return { ok: false, error: SPLASH_FILE_MESSAGES.unreadableVideo };
  }
  if (durationSeconds > MAX_SPLASH_SECONDS) {
    return { ok: false, error: SPLASH_FILE_MESSAGES.videoTooLong(formatSeconds(durationSeconds)) };
  }
  if (width > height) return { ok: false, error: SPLASH_FILE_MESSAGES.videoWide(formatRatio(width, height)) };
  if (Math.abs(width / height / SPLASH_RATIO - 1) > SPLASH_RATIO_TOLERANCE) {
    return { ok: false, error: SPLASH_FILE_MESSAGES.videoShape(formatRatio(width, height)) };
  }

  return {
    ok: true,
    mediaType: "Video",
    contentType: "video/mp4",
    durationSeconds,
    summary: `${file.name} · ${formatSeconds(durationSeconds)} s · ${formatSplashSize(file.size)} · ${width}×${height}`,
  };
}

const isNumber = (value: unknown): value is number => typeof value === "number" && Number.isFinite(value);

/** A Lottie's length in seconds — `(op − ip) / fr` — or `null` when it isn't a Lottie file. */
function lottieSeconds(text: string): number | null {
  let json: unknown;
  try {
    json = JSON.parse(text);
  } catch {
    return null;
  }
  if (!json || typeof json !== "object" || Array.isArray(json)) return null;
  const { v, layers, ip, op, fr } = json as Record<string, unknown>;
  if (v === undefined || !Array.isArray(layers)) return null;
  if (!isNumber(ip) || !isNumber(op) || !isNumber(fr) || fr <= 0) return null;
  return (op - ip) / fr;
}

/** A file's text: `Blob.text()`, or a `FileReader` where the environment has no `text()`. */
export function readBlobText(blob: Blob): Promise<string> {
  if (typeof blob.text === "function") return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

async function readText(file: SplashFileLike): Promise<string | null> {
  try {
    if (file.text) return await file.text();
    if (typeof Blob !== "undefined" && file instanceof Blob) return await readBlobText(file);
  } catch {
    // Unreadable ⇒ not a Lottie file.
  }
  return null;
}

async function checkLottie(file: SplashFileLike): Promise<SplashFileCheck> {
  if (file.size > MAX_LOTTIE_BYTES) return { ok: false, error: SPLASH_FILE_MESSAGES.lottieTooBig };

  const text = await readText(file);
  const seconds = text === null ? null : lottieSeconds(text);
  if (seconds === null) return { ok: false, error: SPLASH_FILE_MESSAGES.notLottie };
  if (seconds > MAX_SPLASH_SECONDS) return { ok: false, error: SPLASH_FILE_MESSAGES.lottieTooLong(formatSeconds(seconds)) };

  return {
    ok: true,
    mediaType: "Lottie",
    contentType: "application/json",
    durationSeconds: seconds,
    summary: `${file.name} · Lottie · ${formatSeconds(seconds)} s · ${formatSplashSize(file.size)}`,
  };
}

/**
 * `{ ok, mediaType, contentType, … }` for a file a splash may use, else `{ ok: false, error }` with
 * its sentence. The video metadata loader is injectable (tests, and the "Try a file" samples).
 */
export async function checkSplashFile(
  file: SplashFileLike,
  loadVideoMetadata: VideoMetadataLoader = (f) =>
    f instanceof Blob ? loadVideoMetadataFromFile(f) : Promise.reject(new Error("Not a browser file"))
): Promise<SplashFileCheck> {
  const kind = kindOf(file);
  if (kind === "video") return checkVideo(file, loadVideoMetadata);
  if (kind === "lottie") return checkLottie(file);
  return { ok: false, error: SPLASH_FILE_MESSAGES.otherType };
}

export interface SplashSampleFile {
  id: "long" | "big" | "wide" | "gif";
  label: string;
  file: SplashFileLike;
  loadVideoMetadata: VideoMetadataLoader;
}

const sampleMetadata =
  (durationSeconds: number, width: number, height: number): VideoMetadataLoader =>
  async () => ({ durationSeconds, width, height });

/**
 * The mock-up's "Try a file" samples (SP-D22): fake file descriptions fed into the same check so
 * staff can see each sentence. A sample is never uploaded and never fills the File field.
 */
export const SPLASH_SAMPLE_FILES: readonly SplashSampleFile[] = [
  {
    id: "long",
    label: "8 s video",
    file: { name: "sample-8s.mp4", type: "video/mp4", size: Math.round(1.4 * MB) },
    loadVideoMetadata: sampleMetadata(8, 1080, 1920),
  },
  {
    id: "big",
    label: "12 MB video",
    file: { name: "sample-12mb.mp4", type: "video/mp4", size: 12 * MB },
    loadVideoMetadata: sampleMetadata(3, 1080, 1920),
  },
  {
    id: "wide",
    label: "Wide video",
    file: { name: "sample-wide.mp4", type: "video/mp4", size: Math.round(1.4 * MB) },
    loadVideoMetadata: sampleMetadata(3, 1920, 1080),
  },
  {
    id: "gif",
    label: "GIF",
    file: { name: "sample.gif", type: "image/gif", size: 800 * 1024 },
    loadVideoMetadata: sampleMetadata(3, 1080, 1920),
  },
];
